/**
 * @fileoverview
 * Variance Check Service - Manages internal step navigation within multi-step audit processes
 * Used for audit processes that have internal steps (e.g., Packet Inventory, Tare Weight)
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class VarianceCheckService {
  private nextStepSubject = new Subject<void>();
  public nextStep$ = this.nextStepSubject.asObservable();

  private stepCompleteSubject = new Subject<boolean>();
  public stepComplete$ = this.stepCompleteSubject.asObservable();

  private currentInternalStepSubject = new BehaviorSubject<number>(1);
  public currentInternalStep$ = this.currentInternalStepSubject.asObservable();

  private nextButtonDisabledSubject = new BehaviorSubject<boolean>(false);
  public nextButtonDisabled$ = this.nextButtonDisabledSubject.asObservable();

  /** Current audit branchId (e.g. for packet inventory Add Excess check-loan-no API) */
  private currentBranchIdSubject = new BehaviorSubject<number | undefined>(undefined);

  /** Current audit id (numeric, for packet inventory Add Excess check-loan-no API) */
  private currentAuditIdSubject = new BehaviorSubject<number | undefined>(undefined);

  /** Packet verification step: bump so parent can re-evaluate Next Step (OnPush + mark-all-as-OK). */
  private packetVerificationNextSubject = new Subject<void>();
  public packetVerificationNext$ = this.packetVerificationNextSubject.asObservable();

  notifyPacketVerificationNextStateChanged(): void {
    this.packetVerificationNextSubject.next();
  }

  /**
   * Variance list total count (e.g. from packet inventory variance summary).
   * Set by variance-check step when summary is loaded; read by acknowledge step to avoid duplicate API call.
   * null = not set (e.g. direct nav to acknowledge).
   */
  private varianceCountSubject = new BehaviorSubject<number | null>(null);

  getVarianceCount(): number | null {
    return this.varianceCountSubject.value;
  }

  setVarianceCount(count: number | null): void {
    this.varianceCountSubject.next(count);
  }

  /**
   * Set the current audit's branchId (used by parent when loading audit; child uses for check-loan-no).
   */
  setCurrentBranchId(branchId: number | undefined): void {
    this.currentBranchIdSubject.next(branchId);
  }

  /**
   * Get the current audit's branchId.
   */
  getCurrentBranchId(): number | undefined {
    return this.currentBranchIdSubject.value;
  }

  /**
   * Set the current audit's id (numeric; used by parent when loading audit; child uses for check-loan-no).
   * Clears variance count when audit changes so acknowledge step does not use stale count from another audit.
   */
  setCurrentAuditId(auditId: number | undefined): void {
    if (this.currentAuditIdSubject.value !== auditId) {
      this.varianceCountSubject.next(null);
    }
    this.currentAuditIdSubject.next(auditId);
  }

  /**
   * Get the current audit's id (numeric).
   */
  getCurrentAuditId(): number | undefined {
    return this.currentAuditIdSubject.value;
  }

  /**
   * Request to move to the next internal step
   */
  requestNextStep() {
    this.nextStepSubject.next();
  }

  /**
   * Notify that the current step is complete
   * @param completed - Whether the step was completed successfully
   */
  notifyStepComplete(completed: boolean) {
    this.stepCompleteSubject.next(completed);
  }

  /**
   * Set the current internal step number
   * @param step - The step number (1-based)
   */
  setCurrentInternalStep(step: number) {
    this.currentInternalStepSubject.next(step);
  }

  /**
   * Get the current internal step number
   * @returns The current step number
   */
  getCurrentInternalStep(): number {
    return this.currentInternalStepSubject.value;
  }

  /**
   * Set whether Next button should be disabled
   * TW-15 AC1, TW-16 AC2: Control Next button state
   */
  setNextButtonDisabled(disabled: boolean) {
    this.nextButtonDisabledSubject.next(disabled);
  }

  /**
   * Get whether Next button should be disabled
   */
  getNextButtonDisabled(): boolean {
    return this.nextButtonDisabledSubject.value;
  }
}

